import { useMemo } from 'react'
import classNames from 'classnames'
import { useLooseProductContextSelector } from 'src/components/restructure/product/contexts/product-context'
import { Image } from 'src/components/ui/Image'
import type { ProductItem } from 'src/components/restructure/product/types/productItem'
import type { Product } from 'src/components/restructure/product/types/product'

import type { Variation } from '../types'

import './color-selector.scss'

interface Props {
  variation: Variation
  onClickVariation: (
    name: string,
    value: string,
    manufacturerCode?: string
  ) => void
  productFromProps?: Product
  isFirstFoldRevampEnabled?: boolean
}

type ItemsByVariantName = Record<string, ProductItem | undefined>

// Duas variações relançadas em anos diferentes podem compartilhar o mesmo
// nome de cor (ex: "azul"), mas pertencer a manufacturerCodes distintos. A chave
// de resolução do item/imagem precisa incluir o manufacturerCode para não colapsar
// as duas opções na mesma imagem.
const buildVariantKey = (name: string, manufacturerCode?: string) =>
  `${name}::${manufacturerCode ?? ''}`

const IMG_SIZE = 64

const STYLES = {
  variationName: 'text-restructure-primary font-medium text-sm font-inter',
  selectedVariationName: 'text-tertiary capitalize',
  selectedVariationNameFirstFold:
    'mt-1 block text-center text-[11px] font-medium leading-[13px] text-[#15181B] capitalize',
  listDefault: 'flex items-center flex-wrap gap-1',
  listRevamp:
    'flex items-start gap-[7px] overflow-x-auto scrollbar-hide restructure-small-desktop:flex-wrap restructure-small-desktop:overflow-visible restructure-small-desktop:gap-1',
  listRevampScope: 'picture-selector-list--first-fold',
  itemBase: 'selector-item shrink-0',
  itemDefault: `w-[${IMG_SIZE}px] h-[${IMG_SIZE}px]`,
  itemRevamp:
    'w-[44px] flex flex-col items-center restructure-small-desktop:w-[64px]',
  buttonRevamp:
    'w-[44px] h-[44px] restructure-small-desktop:w-[64px] restructure-small-desktop:h-[64px]',
  imageBase: 'bg-restructure-background-secondary mix-blend-multiply',
  imageDefault: 'w-full h-full',
  imageRevamp:
    'w-[42px] h-[42px] m-[1px] restructure-small-desktop:w-full restructure-small-desktop:h-full restructure-small-desktop:m-0',
} as const

export function PictureSelector({
  variation,
  onClickVariation,
  productFromProps,
  isFirstFoldRevampEnabled = false,
}: Readonly<Props>) {
  const contextProduct = useLooseProductContextSelector((ctx) => ctx.product)
  const { items } = productFromProps ?? contextProduct

  const variationName = variation.field.name
  const variationId = variation.field.id
  const variationValues = variation.values

  const selectedVariation = variationValues.find(
    (variationValue) => variationValue.selected
  )

  const itemsByVariantName: ItemsByVariantName = useMemo(() => {
    return variationValues.reduce<ItemsByVariantName>((acc, variationValue) => {
      const { name, manufacturerCode } = variationValue
      const matchesValue = (item: ProductItem) =>
        item[variationName]?.[0] === name &&
        (!manufacturerCode || item.manufacturerCode === manufacturerCode)

      const matchedItemWithDiscount = items.find(
        (item) => matchesValue(item) && item.offer.hasDiscount
      )

      const fallbackItem = items.find(matchesValue)

      acc[buildVariantKey(name, manufacturerCode)] =
        matchedItemWithDiscount ?? fallbackItem

      return acc
    }, {})
  }, [items, variationName, variationValues])

  const sortedVariationValues = useMemo(() => {
    return [...variationValues].sort(
      (a, b) => Number(b.isAvailable) - Number(a.isAvailable)
    )
  }, [variationValues])

  const handleClick = (value: string, manufacturerCode?: string) => {
    onClickVariation(variationName, value, manufacturerCode)
  }

  const isFirstFoldRevampStyle =
    variationName === 'Cor' && isFirstFoldRevampEnabled

  const listClassName = isFirstFoldRevampStyle
    ? STYLES.listRevamp
    : STYLES.listDefault

  const itemSizeClassName = isFirstFoldRevampStyle
    ? STYLES.itemRevamp
    : STYLES.itemDefault

  const imageSizeClassName = isFirstFoldRevampStyle
    ? STYLES.imageRevamp
    : STYLES.imageDefault

  const showVariationHeader = !isFirstFoldRevampStyle

  return (
    <div>
      {(showVariationHeader || isFirstFoldRevampStyle) && (
        <span
          className={classNames(STYLES.variationName, {
            'hidden restructure-small-desktop:block': isFirstFoldRevampStyle,
          })}
        >
          {variationName}{' '}
          <span className={STYLES.selectedVariationName}>
            {selectedVariation?.name.split('-')[0].toLowerCase()}
          </span>
        </span>
      )}

      {variation?.values?.length > 1 ? (
        <ul
          className={classNames('mt-4', listClassName, {
            [STYLES.listRevampScope]: isFirstFoldRevampStyle,
          })}
          key={variationId}
          data-testid="picture-selector"
        >
          {sortedVariationValues.map((value, index) => {
            const item =
              itemsByVariantName[
                buildVariantKey(value.name, value.manufacturerCode)
              ]

            const image = item?.images?.[0]
            const variationLabel = value.name.split('-')[0].toLowerCase()

            const hasDiscount = item?.offer.hasDiscount && value.isAvailable

            return (
              <li
                key={value.id}
                className={classNames(STYLES.itemBase, itemSizeClassName)}
              >
                <button
                  className={classNames(
                    'color-selector-btn',
                    {
                      selected: value.selected,
                      disabled: !value.isAvailable,
                      'has-discount': hasDiscount,
                    },
                    {
                      [STYLES.buttonRevamp]: isFirstFoldRevampStyle,
                    }
                  )}
                  onClick={() =>
                    handleClick(value.name, value.manufacturerCode)
                  }
                  title={value.name}
                  disabled={!value.isAvailable}
                  data-testid={`variant-color-${index}`}
                >
                  {image && (
                    <Image
                      key={image.imageUrl}
                      src={image.imageUrl}
                      width={56}
                      height={56}
                      alt={image.imageText}
                      style={{ objectFit: 'contain' }}
                      preload
                      className={classNames(
                        STYLES.imageBase,
                        imageSizeClassName
                      )}
                    />
                  )}
                </button>

                {isFirstFoldRevampStyle && value.selected && (
                  <span
                    className={`${STYLES.selectedVariationNameFirstFold} restructure-small-desktop:hidden`}
                  >
                    {variationLabel}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
